import type { BaseProps } from './base-props';

/**
 * Fill and stroke properties
 */
export interface FillStrokeProps {
  /**
   * Object fill color
   * Supports CSS colors: hex, rgb, rgba, hsl, hsla, named colors
   * @default '#000000'
   */
  fill: string | null;

  /**
   * Fill rule for paths (nonzero or evenodd)
   * @default 'nonzero'
   */
  fillRule: CanvasFillRule;

  /**
   * Object stroke color
   * @default null
   */
  stroke: string | null;

  /**
   * Stroke width
   * @default 1
   */
  strokeWidth: number;

  /**
   * Dash pattern array
   * @default null
   */
  strokeDashArray: number[] | null;

  /**
   * Dash pattern offset
   * @default 0
   */
  strokeDashOffset: number;

  /**
   * Line cap style
   * @default 'butt'
   */
  strokeLineCap: CanvasLineCap;

  /**
   * Line join style
   * @default 'miter'
   */
  strokeLineJoin: CanvasLineJoin;

  /**
   * Maximum miter length
   * @default 4
   */
  strokeMiterLimit: number;

  /**
   * What to paint first: fill or stroke
   * @default 'fill'
   */
  paintFirst: 'fill' | 'stroke';
}

/**
 * Object rendering properties
 */
export interface RenderProps {
  /**
   * Object opacity (0-1)
   * @default 1
   */
  opacity: number;

  /**
   * Compositing mode
   * @default 'source-over'
   */
  globalCompositeOperation: GlobalCompositeOperation;

  /**
   * Object background color
   * @default ''
   */
  backgroundColor: string;

  /**
   * Object visibility
   * @default true
   */
  visible: boolean;

  /**
   * Object shadow (a string for now, for SSR compatibility)
   * A Shadow class could be added in the future
   * @default null
   */
  shadow: string | null;
}

/**
 * Serialization properties
 */
export interface SerializationProps {
  /**
   * Whether to include default values in serialization
   * @default true
   */
  includeDefaultValues: boolean;

  /**
   * Exclude the object from export
   * @default false
   */
  excludeFromExport: boolean;
}

/**
 * Object state properties
 */
export interface StateProps {
  /**
   * Unique object identifier
   */
  id?: string;

  /**
   * Object type (rect, circle, path, etc.)
   * @default 'object'
   */
  type: string;

  /**
   * Custom user data
   */
  data?: Record<string, unknown>;

  /**
   * Flag indicating the object has changed
   * Used for rendering optimization
   * @default true
   */
  dirty: boolean;
}

/**
 * Full set of editor object properties
 *
 * Combines all property interfaces into one
 * SSR-compatible - does not use browser-only APIs
 */
export interface ObjectProps
  extends BaseProps,
    FillStrokeProps,
    RenderProps,
    SerializationProps,
    StateProps {}

/**
 * Default values for fill and stroke properties
 */
export const fillStrokePropsDefaults: FillStrokeProps = {
  fill: '#000000',
  fillRule: 'nonzero',
  stroke: null,
  strokeWidth: 1,
  strokeDashArray: null,
  strokeDashOffset: 0,
  strokeLineCap: 'butt',
  strokeLineJoin: 'miter',
  strokeMiterLimit: 4,
  paintFirst: 'fill',
};

/**
 * Default values for rendering properties
 */
export const renderPropsDefaults: RenderProps = {
  opacity: 1,
  globalCompositeOperation: 'source-over',
  backgroundColor: '',
  visible: true,
  shadow: null,
};

/**
 * Default values for serialization properties
 */
export const serializationPropsDefaults: SerializationProps = {
  includeDefaultValues: true,
  excludeFromExport: false,
};

/**
 * Default values for state properties
 */
export const statePropsDefaults: StateProps = {
  type: 'object',
  dirty: true,
};
